import { createRoot } from "react-dom/client";
import { toast } from "sonner";
import App from "./App.tsx";
import "./index.css";
import "./lib/installPrompt"; // capture the install prompt early (shown only on user tap)

// PWA lifecycle. The service worker is registered directly (no
// virtual:pwa-register — its prompt mode installs its own unconditional
// "controlling" reload). EVERY activation and reload goes through the
// UpdateCoordinator safe gate in src/lib/updateCoordinator.ts.
import {
  CHECK_INTERVAL_MS,
  MIGRATION_KEY,
  UPDATE_CHANNEL,
  fetchBuildId,
  isAppCacheName,
  isCacheableImageRequest,
  isServiceWorkerAllowed,
  makeThrottled,
} from "./lib/pwaUpdate";
import { UpdateCoordinator, type WorkerControl, type WorkerStatus } from "./lib/updateCoordinator";

declare const __ANAYA_BUILD_ID__: string;
const BUILD_ID: string = typeof __ANAYA_BUILD_ID__ === "string" ? __ANAYA_BUILD_ID__ : "dev";
const SW_URL = "/sw.js";

const swSupported = "serviceWorker" in navigator;
const swAllowed =
  swSupported &&
  isServiceWorkerAllowed({
    prod: import.meta.env.PROD,
    inIframe: (() => {
      try {
        return window.self !== window.top;
      } catch {
        return true;
      }
    })(),
    hostname: window.location.hostname,
    search: window.location.search,
  });

// Dirty-form detection: any typed/changed input marks the session dirty until
// a submit or a page navigation resets it. Protects forms with no draft.
let formDirty = false;
window.addEventListener(
  "input",
  (e) => {
    const t = e.target;
    if (
      t instanceof HTMLInputElement ||
      t instanceof HTMLTextAreaElement ||
      t instanceof HTMLSelectElement
    ) {
      formDirty = true;
    }
  },
  true,
);
window.addEventListener("submit", () => { formDirty = false; }, true);

const withTimeout = <T,>(p: Promise<T>, ms: number, fallback: T): Promise<T> =>
  new Promise((resolve) => {
    const timer = setTimeout(() => resolve(fallback), ms);
    p.then(
      (v) => { clearTimeout(timer); resolve(v); },
      () => { clearTimeout(timer); resolve(fallback); },
    );
  });

const getRegistration = () =>
  withTimeout(navigator.serviceWorker.getRegistration("/"), 5_000, undefined);

const workerControl: WorkerControl = {
  async status(): Promise<WorkerStatus> {
    const reg = await getRegistration();
    if (!reg) return "none";
    if (!reg.installing && !reg.waiting) {
      await withTimeout(reg.update().then(() => undefined), 10_000, undefined);
    }
    if (reg.waiting) return "waiting";
    if (reg.installing) return "installing";
    return reg.active ? "current" : "none";
  },
  async waitUntilInstalled(timeoutMs) {
    const reg = await getRegistration();
    const sw = reg?.installing;
    if (!sw) return true;
    return new Promise<boolean>((resolve) => {
      const done = (ok: boolean) => {
        clearTimeout(timer);
        sw.removeEventListener("statechange", onChange);
        resolve(ok);
      };
      const onChange = () => {
        if (sw.state === "installed" || sw.state === "activating" || sw.state === "activated") done(true);
        else if (sw.state === "redundant") done(false);
      };
      const timer = setTimeout(() => done(false), timeoutMs);
      sw.addEventListener("statechange", onChange);
      onChange();
    });
  },
  waitForControllerChange(timeoutMs) {
    return new Promise<boolean>((resolve) => {
      const onChange = () => {
        clearTimeout(timer);
        resolve(true);
      };
      const timer = setTimeout(() => {
        navigator.serviceWorker.removeEventListener("controllerchange", onChange);
        resolve(false);
      }, timeoutMs);
      navigator.serviceWorker.addEventListener("controllerchange", onChange, { once: true });
    });
  },
  async requestActivation() {
    const reg = await getRegistration();
    reg?.waiting?.postMessage({ type: "SKIP_WAITING" });
  },
};

// Multi-tab: tabs announce the build they reload into; peers verify the real
// deployed id themselves before going through their own gate.
const updateChannel =
  "BroadcastChannel" in window ? new BroadcastChannel(UPDATE_CHANNEL) : null;

const fetchLatestBuildId = () => fetchBuildId(fetch);

const coordinator = new UpdateCoordinator({
  getPathname: () => window.location.pathname,
  getStoredItem: (k) => sessionStorage.getItem(k),
  isFormDirty: () => formDirty,
  reload: () => window.location.reload(),
  fetchLatestBuildId,
  worker: swAllowed ? workerControl : null,
  announce: (buildId) => updateChannel?.postMessage({ type: "reloading", buildId }),
  notify: ({ title, description, actionLabel, onAction }) =>
    toast(title, {
      description,
      duration: Infinity,
      action: actionLabel ? { label: actionLabel, onClick: () => onAction?.() } : undefined,
    }),
  storage: sessionStorage,
  currentBuildId: BUILD_ID,
});

updateChannel?.addEventListener("message", (event) => {
  const data = event.data as { type?: string; buildId?: unknown } | null;
  if (data?.type === "reloading") void coordinator.onPeerReloading(data.buildId);
});

// Retry deferred updates on real SPA route changes, focus and visibility.
let lastPath = window.location.pathname;
const onLocationMaybeChanged = (navigated: boolean) => {
  setTimeout(() => {
    const path = window.location.pathname;
    if (path === lastPath) return;
    lastPath = path;
    if (navigated) formDirty = false; // leaving a page discards its inputs
    void coordinator.onRouteChange();
  }, 0);
};
const origPushState = history.pushState.bind(history);
history.pushState = (...args) => {
  const r = origPushState(...args);
  onLocationMaybeChanged(true);
  return r;
};
const origReplaceState = history.replaceState.bind(history);
history.replaceState = (...args) => {
  const r = origReplaceState(...args);
  onLocationMaybeChanged(false);
  return r;
};
window.addEventListener("popstate", () => onLocationMaybeChanged(true));
window.addEventListener("focus", () => void coordinator.tryApply());
document.addEventListener("visibilitychange", () => {
  if (document.visibilityState === "visible") void coordinator.tryApply();
});

/**
 * Release freshness check: compare the running build id with /version.json
 * (never precached, fetched no-store). On mismatch poke the worker so the new
 * one installs, then hand the REAL target id to the gate. Failures are ignored.
 */
const checkForNewRelease = makeThrottled(async () => {
  const latest = await fetchLatestBuildId();
  if (!latest || latest === BUILD_ID) return;
  if (swAllowed) {
    const reg = await getRegistration();
    await withTimeout((reg?.update() ?? Promise.resolve()).then(() => undefined), 10_000, undefined);
  }
  await coordinator.onUpdateAvailable(latest);
}, CHECK_INTERVAL_MS);

async function registerServiceWorker() {
  try {
    const reg = await navigator.serviceWorker.register(SW_URL, { scope: "/" });
    const watch = (sw: ServiceWorker | null) => {
      if (!sw) return;
      const onState = () => {
        // Installed while a controller exists = an update is waiting.
        if (sw.state === "installed" && navigator.serviceWorker.controller) {
          void coordinator.onWorkerWaiting();
        }
      };
      sw.addEventListener("statechange", onState);
      onState();
    };
    if (reg.waiting && navigator.serviceWorker.controller) void coordinator.onWorkerWaiting();
    watch(reg.installing);
    reg.addEventListener("updatefound", () => watch(reg.installing));
  } catch (e) {
    console.warn("[PWA] registration failed", e);
  }
}

async function unregisterAppWorkers() {
  try {
    const regs = await navigator.serviceWorker.getRegistrations();
    await Promise.all(
      regs
        .filter((r) =>
          [r.active, r.waiting, r.installing].some((w) => w?.scriptURL.endsWith(SW_URL)),
        )
        .map((r) => r.unregister()),
    );
  } catch {
    // ignore
  }
}

if (swAllowed) {
  void registerServiceWorker();
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    void coordinator.onControllerChange();
  });
} else if (swSupported) {
  void unregisterAppWorkers();
}

if (import.meta.env.PROD) {
  void checkForNewRelease();
  window.addEventListener("focus", () => void checkForNewRelease());
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") void checkForNewRelease();
  });
  window.addEventListener("online", () => void checkForNewRelease());
  setInterval(() => void checkForNewRelease(), CHECK_INTERVAL_MS);
}

/**
 * One-time migration for browsers that ran the previous worker generation.
 * Runs after the current worker is ready; deletes only obsolete ANAYA runtime
 * caches (never storage, never foreign caches) and purges image entries the
 * new privacy rule no longer allows. Marked done only after success.
 */
const ACTIVE_CACHES = new Set([
  "anaya-pages-cache",
  "images-cache",
  "google-fonts-cache",
  "gstatic-fonts-cache",
]);

async function migrateLegacyCaches() {
  if (!swAllowed || !("caches" in window)) return;
  try {
    if (localStorage.getItem(MIGRATION_KEY)) return;
    await navigator.serviceWorker.ready;
    const names = await caches.keys();
    const obsolete = names.filter(
      (n) => isAppCacheName(n) && !ACTIVE_CACHES.has(n) && !/precache/.test(n),
    );
    await Promise.all(obsolete.map((n) => caches.delete(n)));
    if (names.includes("images-cache")) {
      const images = await caches.open("images-cache");
      const keys = await images.keys();
      await Promise.all(
        keys
          .filter((req) => {
            const url = new URL(req.url);
            return !isCacheableImageRequest({
              url,
              request: req,
              sameOrigin: url.origin === window.location.origin,
            });
          })
          .map((req) => images.delete(req)),
      );
    }
    localStorage.setItem(MIGRATION_KEY, "1");
  } catch (e) {
    console.warn("[PWA] legacy migration failed", e);
  }
}
void migrateLegacyCaches();

// A dynamic-import failure means an old shell references deleted assets.
// Same gate, but only if a newer build is really deployed.
window.addEventListener("vite:preloadError", (event) => {
  event.preventDefault();
  void coordinator.onChunkError();
});

createRoot(document.getElementById("root")!).render(<App />);
